/**
 * WL-K9 — Client wrappers for the Exception-First-StB-Cockpit callables.
 *
 * Thin httpsCallable bindings (same pattern as {@link ./advisorInviteApi}). ALL hard
 * security checks live server-side (`functions/src/moat/advisorCockpit.ts`):
 * Advisor-Scope + Loop-B-Beta enforcement, mandate resolution and cross-tenant
 * reads all happen in the callable — these helpers only marshal input/output and
 * normalise errors. The cockpit is strictly READ-ONLY (no write anywhere).
 *
 * @module api/advisorCockpitApi
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

/** Traffic-light severity of a cockpit item (mirror of the server `CockpitAmpel`). */
export type CockpitAmpel = 'green' | 'yellow' | 'red';

/**
 * WL-B (Welle 1 Cockpit-Intelligenz) — current resolution state of a single
 * finding. `null` on {@link CockpitFinding.resolution} means the finding is
 * still active (never resolved, or `reopened` — architecture §Datenmodelle
 * #2: "'reopened' ⇒ null (=aktiv)").
 */
export interface CockpitFindingResolution {
  resolutionType: 'resolved' | 'ignored';
  resolvedByRole: 'owner' | 'advisor';
  note: string;
  /** Server `Timestamp` → FE millis (architecture §DB-Mapping-Layer). */
  resolvedAt: number;
}

/** One flagged finding (the reason a beleg is on the worklist). */
export interface CockpitFinding {
  checkId: string;
  ampel: CockpitAmpel;
  code: string;
  /**
   * WL-B: server-computed, deterministic fingerprint binding this finding to
   * its EXACT state (`docId|checkId|code|version|contentKey`) — the value
   * `resolveCheckFinding` needs to keep the audit trail reactive to re-scans.
   */
  fingerprint: string;
  /** WL-B: current resolution, or `null` while the finding is active. */
  resolution: CockpitFindingResolution | null;
}

/** One ranked worklist entry (one flagged inbound document). */
export interface CockpitItem {
  docId: string;
  ampel: CockpitAmpel;
  findings: CockpitFinding[];
  findingCount: number;
  supplierName: string;
  documentDate: string;
  invoiceNumber: string;
  grossTotal: number | null;
  currency: string;
  verifiedAgainstOriginal: boolean;
  verificationSource: string | null;
  score: number;
  /** WL-B: how many of this document's ORIGINAL findings are currently resolved/ignored. */
  resolvedFindingCount: number;
}

/** Ranked worklist for one mandant (server `CockpitWorklistResult`). */
export interface CockpitWorklistResult {
  mandantUid: string;
  totalChecked: number;
  flaggedCount: number;
  items: CockpitItem[];
  redCount: number;
  yellowCount: number;
}

/** One mandant row of the overview (server `CockpitMandantSummary`). */
export interface CockpitMandantSummary {
  mandantUid: string;
  displayName: string | null;
  redCount: number;
  yellowCount: number;
  flaggedCount: number;
  totalChecked: number;
}

/** Response of `getAdvisorCockpitMandanten`. */
export interface CockpitMandantenResult {
  mandanten: CockpitMandantSummary[];
}

/**
 * Loads the caller's per-mandant cockpit overview. The server resolves the
 * advisor's active memberships and returns only the beta mandants (non-beta and
 * out-of-scope mandants are never returned). Returns an empty list when the caller
 * advises nobody.
 *
 * @returns the per-mandant summary rows.
 * @throws Error (normalised) on unexpected server failures.
 */
export async function getAdvisorCockpitMandanten(): Promise<CockpitMandantenResult> {
  const callable = httpsCallable<Record<string, never>, CockpitMandantenResult>(
    functions,
    'getAdvisorCockpitMandanten'
  );
  try {
    const res = await callable({});
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Loads the ranked worklist for ONE mandant. The server enforces that the caller is
 * an authorized, beta-scoped advisor of `mandantUid`; a foreign mandant yields
 * `permission-denied`.
 *
 * @param mandantUid the mandant's scope uid (from the overview / tenant switcher).
 * @returns the ranked worklist (empty `items` ⇒ the „alles grün"-Zustand).
 * @throws Error (normalised) with `code==='permission-denied'` for a foreign
 *   mandant, or on unexpected server failures.
 */
export async function getAdvisorCockpitWorklist(
  mandantUid: string
): Promise<CockpitWorklistResult> {
  const callable = httpsCallable<{ mandantUid: string }, CockpitWorklistResult>(
    functions,
    'getAdvisorCockpitWorklist'
  );
  try {
    const res = await callable({ mandantUid });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Maps a Firebase callable error to a plain Error, preserving the server message and
 * exposing the coarse error code (e.g. `permission-denied`) without leaking internals.
 *
 * @param err the raw callable rejection.
 * @returns a plain Error carrying an optional `code`.
 */
function normalizeCallableError(err: unknown): Error & { code?: string } {
  const anyErr = err as { code?: string; message?: string };
  const message =
    typeof anyErr?.message === 'string' && anyErr.message.length > 0
      ? anyErr.message
      : 'Request failed.';
  const out = new Error(message) as Error & { code?: string };
  if (typeof anyErr?.code === 'string') {
    out.code = anyErr.code.replace(/^functions\//, '');
  }
  return out;
}
